/**
 * Updates the primary diagnosis ICD code on a medical PA order in Firestore.
 *
 * @module updatePrimaryDiagnosisCode
 */
import { PrimaryDiagnosisDetails } from "../../data-model/medicalPaOrdersModel";
import { SingleOrderDetailsTableName } from "../../enums/singleOrderDetailsTableName";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { singleOrderDetailsGenericUpdateForPostgres } from "../postgreSQL/postCalls/singleOrderDetailsGenericUpdateForPostgres";

/** User-provided justification captured when the primary Dx is changed. */
export interface DiagnosisChangeContext {
  /** Selected reason option, including the "Other" sentinel when chosen. */
  userReason?: string;
  /** Selected source option, including the "Other" sentinel when chosen. */
  userSource?: string;
  /** Free-text reason captured only when the reason option is "Other". */
  otherReason?: string;
  /** Free-text source captured only when the source option is "Other". */
  otherSource?: string;
}

/**
 * Extracts the existing `primary_diagnosis_details` array from an order
 * document, defaulting to an empty array when absent or malformed.
 *
 * @param doc - The raw Firestore order document
 * @returns A shallow copy of the existing diagnosis entries
 */
const extractExistingDiagnoses = (doc: any): PrimaryDiagnosisDetails[] => {
  if (Array.isArray(doc)) {
    return [...doc];
  }

  if (
    doc.primary_diagnosis_details &&
    Array.isArray(doc.primary_diagnosis_details)
  ) {
    return [...doc.primary_diagnosis_details];
  }
  return [];
};

/**
 * Builds the change-only fields (reason/source) to layer onto a diagnosis
 * entry, omitting any field the user left blank.
 *
 * @param changeContext - Optional user-provided reason/source for the change
 * @returns A partial diagnosis object with only the provided change fields
 */
const buildChangeFields = (
  changeContext?: DiagnosisChangeContext,
): Partial<PrimaryDiagnosisDetails> => ({
  // Keep dropdown selections and free-text "Other" details as separate
  // persisted fields so downstream consumers can distinguish both signals.
  ...(changeContext?.userReason
    ? { user_reason: changeContext.userReason }
    : {}),
  ...(changeContext?.userSource
    ? { user_source: changeContext.userSource }
    : {}),
  ...(changeContext?.otherReason
    ? { other_reason: changeContext.otherReason }
    : {}),
  ...(changeContext?.otherSource
    ? { other_source: changeContext.otherSource }
    : {}),
});

/**
 * Collapses the diagnosis array to a single entry, preserving all other fields
 * of the existing first entry and layering the new ICD code, description, and
 * user change context on top.
 *
 * @param existing - The current diagnosis entries (may be empty)
 * @param icdCode - New ICD code to set
 * @param icdDescription - Optional human-readable ICD description
 * @param changeContext - Optional user-provided reason/source for the change
 * @returns A single-element diagnosis array preserving prior fields
 */
const buildOverwrittenDiagnoses = (
  existing: PrimaryDiagnosisDetails[],
  icdCode: string,
  icdDescription?: string,
  changeContext?: DiagnosisChangeContext,
): PrimaryDiagnosisDetails[] => [
  {
    diagnosis: "",
    ...(existing[0] ?? {}),
    icd_code: icdCode,
    icd_description: icdDescription || "",
    ...buildChangeFields(changeContext),
  },
];

/**
 * Patches the first entry of an existing diagnosis array with a new ICD code,
 * creating the entry if none exists.
 *
 * @param existing - The current diagnosis entries (may be empty)
 * @param icdCode - New ICD code to apply to the first entry
 * @param changeContext - Optional user-provided reason/source for the change
 * @returns The updated diagnosis array
 */
const patchFirstDiagnosis = (
  existing: PrimaryDiagnosisDetails[],
  icdCode: string,
  changeContext?: DiagnosisChangeContext,
): PrimaryDiagnosisDetails[] => {
  const updated = [...existing];
  const changeFields = buildChangeFields(changeContext);

  updated[0] = existing[0]
    ? { ...existing[0], icd_code: icdCode, ...changeFields }
    : {
        diagnosis: "",
        icd_code: icdCode,
        icd_description: "",
        ...changeFields,
      };

  return updated;
};

/**
 * Persists the updated diagnosis array back to Firestore.
 *
 * @param orderId - The order document ID
 * @param diagnoses - The diagnosis array to save
 */
const saveDiagnoses = async (
  orderId: string,
  diagnoses: PrimaryDiagnosisDetails[],
  sibling_order_ids: string[] = [],
): Promise<void> => {
  await singleOrderDetailsGenericUpdateForPostgres({
    order_id: orderId,
    org_id: getOrgIdForFetchExternalWorklist(),
    table_name: SingleOrderDetailsTableName.SUBMISSION,
    data: {
      primary_diagnosis_details: diagnoses,
    },
    sibling_order_ids: sibling_order_ids,
  });
};

/**
 * Updates the primary diagnosis ICD code on a medical PA order.
 *
 * When `overwrite` is `true` the diagnosis array is collapsed to a single
 * entry that preserves all other fields of the existing first entry, with the
 * new ICD code, description, and change context layered on top. Otherwise only
 * the `icd_code` on the first existing entry is patched (or a new first entry
 * is created).
 *
 * @param orderId - Firestore document ID of the medical PA order
 * @param newIcdCode - The ICD code to set
 * @param newIcdDescription - Optional description for the ICD code (used only when overwriting)
 * @param overwrite - When `true`, replaces all existing diagnosis entries
 * @param changeContext - Optional user-provided reason/source for the change
 * @throws {Error} If the order document cannot be found or the write fails
 */
export const updatePrimaryDiagnosisCode = async (
  orderId: string,
  primaryDiagnosisDetails: PrimaryDiagnosisDetails[],
  newIcdCode: string,
  newIcdDescription?: string,
  overwrite: boolean = false,
  changeContext?: DiagnosisChangeContext,
  sibling_order_ids: string[] = [],
): Promise<void> => {
  const existing = extractExistingDiagnoses(primaryDiagnosisDetails);

  const updated = overwrite
    ? buildOverwrittenDiagnoses(
        existing,
        newIcdCode,
        newIcdDescription,
        changeContext,
      )
    : patchFirstDiagnosis(existing, newIcdCode, changeContext);

  await saveDiagnoses(orderId, updated, sibling_order_ids);
};
